import React, { useCallback, useRef } from 'react';
import './style.css';
import { useSelector, useDispatch } from 'react-redux';

import Symbols from '../../../../../utilities/mappings/symbols/Symbols';
import { refreshSymbols, setExclusiveCategoryOpen, setOrientation } from '../../../../../redux/reducers/global/editor/editorSlice';
import { prepareDragPayload, buildSymbolPayload, DEFAULT_ICON_SCALE_CM } from '../../../../../utilities/dragHelpers';
import { HORIZONTAL_ORIENTATION, VERTICAL_ORIENTATION } from '../../../../../utilities/constants/editorConstants';
import { clearSpawnPreview, requestSpawnSymbol, resetBoxTool, setSpawnPreview } from '../../../../../redux/reducers/global/view/viewSlice';

const EditorTabPanelElements = ({ /* props */ }) => {

    const dispatch = useDispatch();
    const angleOffset = useSelector((state) => state.editor.angleOffset);
    const categories = useSelector((state) => state.editor.categories);
    const searchQuery = useSelector((state) => state.editor.searchQuery);
    const searchResults = useSelector((state) => state.editor.searchResults);
    const forceSearchResults = useSelector((state) => state.editor.forceSearchResults);
    const selectedIndex = useSelector((state) => state.editor.searchSelectedIndex) || 0;
    const symbolsCatalog = useSelector((state) => state.editor.symbols) || {};
    const toolsScrollRef = useRef(null);

    React.useEffect(() => {
        dispatch(refreshSymbols());
    }, [dispatch]);

    const handleDragStart = (event, group, baseAlias, variantAlias, overrideAngle, isRotable, symbolLabel) => {
        const effectiveAngle = typeof overrideAngle === 'number' ? overrideAngle : angleOffset;
        dispatch(clearSpawnPreview());
        prepareDragPayload(event, group, baseAlias, effectiveAngle, isRotable, symbolLabel, variantAlias, DEFAULT_ICON_SCALE_CM);
    };

    const buildPayloadForEntry = useCallback(
        (entry, category, overrideAngle) => {
            if (!entry || !category) return null;
            const rotable = entry.rotable ?? entry.source?.rotable ?? false;
            const baseAlias = entry.baseAlias || entry.alias;
            const variantAlias = Object.prototype.hasOwnProperty.call(entry, 'variantAlias')
                ? entry.variantAlias
                : (entry.activeVariantAlias || null);
            const angle = typeof overrideAngle === 'number' ? overrideAngle : angleOffset;
            const symbolKey = entry.symbolLabel || entry.type || entry.baseLabel || entry.label || 'Custom';
            return buildSymbolPayload(
                category,
                baseAlias,
                angle,
                rotable,
                symbolKey,
                variantAlias,
                DEFAULT_ICON_SCALE_CM
            );
        },
        [angleOffset]
    );

    const handlePaletteSingleClick = useCallback(
        (entry, category) => {
            dispatch(resetBoxTool());
            const payload = buildPayloadForEntry(entry, category);
            if (!payload) return;
            dispatch(setSpawnPreview({ data: payload }));
        },
        [buildPayloadForEntry, dispatch]
    );

    const handlePaletteDoubleClick = useCallback(
        (entry, category) => {
            dispatch(resetBoxTool());
            const payload = buildPayloadForEntry(entry, category);
            if (!payload) return;
            dispatch(clearSpawnPreview());
            dispatch(requestSpawnSymbol({ data: payload, placementMode: 'center' }));
        },
        [buildPayloadForEntry, dispatch]
    );

    const toggleSymbolsRotation = useCallback(() => {
        const nextAngle = angleOffset === VERTICAL_ORIENTATION
            ? HORIZONTAL_ORIENTATION
            : VERTICAL_ORIENTATION;
        dispatch(setOrientation({ angle: nextAngle }));
    }, [dispatch, angleOffset]);

    const Dropdown = React.memo(({ children, title, isOpen, onClick}) => {
        
        const iconClassName = isOpen ? "symbol_dropdown__icon rotate" : "symbol_dropdown__icon";
    
        return (
            <div className="symbol_dropdown">
                <button onClick={onClick}>
                    <div className="symbol_dropdown__title">
                        {title}
                    </div>
                    <div className={iconClassName}></div>
                </button>
                {isOpen && (
                    <div className="symbol_dropdown__options">
                        {children}
                    </div>
                )}
            </div>
        );
    });

    const CategoryItem = React.memo(({ entry, angleOffset, onDragStart, category, displayIndex, onSingleClick, onDoubleClick }) => {
        const rotable = entry.rotable ?? entry.source?.rotable ?? false;
        const transform = rotable ? `rotate(${angleOffset}deg)` : ``;
        const symbolLabel = entry.symbolLabel || entry.type;
        const source = entry.source || entry;
        const hasVariants = Array.isArray(source?.variants) && source.variants.length > 0;
        const displayLabel = entry.displayLabel || entry.label || source?.label || 'Símbolo';
        const svgSrc = entry.svgSrc || entry.diagramSvgSrc || source?.svgSrc;
        const [isPressed, setIsPressed] = React.useState(false);
        const className = [
            'dndnode',
            hasVariants ? 'variants' : '',
            isPressed ? 'is-pressed' : '',
        ].filter(Boolean).join(' ');
        const suffix = typeof displayIndex === 'number'
            ? <span className="search-shortcut"> ({displayIndex})</span>
            : null;
    
        const baseAlias = entry.baseAlias || entry.alias;
        const variantAlias = Object.prototype.hasOwnProperty.call(entry, 'variantAlias')
          ? entry.variantAlias
          : null;
        const dragIntentRef = useRef(false); // Distinguish click from drag interactions

        React.useEffect(() => {
            if (!isPressed) return undefined;

            const clearPressedState = () => {
                if (!dragIntentRef.current) {
                    setIsPressed(false);
                }
            };

            window.addEventListener('mouseup', clearPressedState);
            return () => {
                window.removeEventListener('mouseup', clearPressedState);
            };
        }, [isPressed]);

        React.useEffect(() => {
            if (!isPressed) return undefined;

            const clearAfterEditorDrop = () => {
                dragIntentRef.current = false;
                setIsPressed(false);
            };

            window.addEventListener('sololine:symbol-drop-complete', clearAfterEditorDrop);
            return () => {
                window.removeEventListener('sololine:symbol-drop-complete', clearAfterEditorDrop);
            };
        }, [isPressed]);

        React.useEffect(() => {
            if (!isPressed) return undefined;

            const clearOnEscape = (event) => {
                if (event.key !== 'Escape') return;
                dragIntentRef.current = false;
                setIsPressed(false);
                dispatch(clearSpawnPreview());
            };

            window.addEventListener('keydown', clearOnEscape, true);
            return () => {
                window.removeEventListener('keydown', clearOnEscape, true);
            };
        }, [isPressed]);

        const handleMouseDown = useCallback((event) => {
            if (event.button !== 0) return;
            dragIntentRef.current = false;
            setIsPressed(true);
        }, []);

        const handleDragStart = useCallback(
            (event) => {
                dragIntentRef.current = true;
                setIsPressed(true);
                onDragStart(
                    event,
                    category,
                    baseAlias,
                    variantAlias,
                    angleOffset,
                    rotable,
                    symbolLabel
                );
            },
            [onDragStart, category, baseAlias, variantAlias, angleOffset, rotable, symbolLabel]
        );

        const handleClick = useCallback(
            (event) => {
                const isKeyboardActivation = event.detail === 0;
                const isPrimaryClick = event.detail === 1;
                if (!isKeyboardActivation && !isPrimaryClick) {
                    return;
                }
                if (dragIntentRef.current) {
                    dragIntentRef.current = false;
                    return;
                }
                if (typeof onSingleClick === 'function') {
                    onSingleClick(entry, category);
                }
                setIsPressed(true);
            },
            [onSingleClick, entry, category]
        );

        const handleDragEnd = useCallback(() => {
            dragIntentRef.current = false;
            setIsPressed(false);
            window.dispatchEvent(new CustomEvent('sololine:symbol-drag-end'));
        }, []);

        const handleMouseUp = useCallback(() => {
            if (!dragIntentRef.current) {
                setIsPressed(false);
            }
        }, []);

        return (
            <div
                className={className}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onClick={handleClick}
                onDoubleClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    dragIntentRef.current = false;
                    onDoubleClick(entry, category);
                }}
                draggable
            >
                <img src={svgSrc} alt={displayLabel} style={{ 
                    transform: transform,
                    transition: `all 0.3s ease`,
                    height: "60px" 
                }} />
                <p>{displayLabel}{suffix}</p>
            </div>
        );
    });
    
    const buildDisplayEntries = (items) => {
        const entries = [];
        items.forEach((item) => {
            const isSerializedEntry =
                Object.prototype.hasOwnProperty.call(item || {}, 'displayLabel') ||
                Object.prototype.hasOwnProperty.call(item || {}, 'baseAlias');
            if (isSerializedEntry) {
                entries.push({
                    key: `${item.baseAlias || item.alias}::${item.variantAlias || item.alias}`,
                    baseAlias: item.baseAlias || item.alias,
                    variantAlias: Object.prototype.hasOwnProperty.call(item, 'variantAlias') ? item.variantAlias : null,
                    displayLabel: item.displayLabel || item.label,
                    svgSrc: item.svgSrc || item.diagramSvgSrc,
                    rotable: item.rotable,
                    symbolLabel: item.symbolLabel || item.type,
                    source: item,
                });
                return;
            }
            const hasVariants = Array.isArray(item?.variants) && item.variants.length > 0;
            if (hasVariants) {
                item.variants.forEach((variant, idx) => {
                    const displayLabel = variant.label
                        ? `${item.label} - ${variant.label}`
                        : item.label;
                    entries.push({
                        key: `${item.alias}::${variant.alias || idx}`,
                        baseAlias: item.alias,
                        variantAlias: variant.alias,
                        displayLabel,
                        svgSrc: variant.diagramSvgSrc || item.svgSrc,
                        rotable: item.rotable,
                        symbolLabel: item.type,
                        source: item,
                    });
                });
            } else {
                entries.push({
                    key: item.alias,
                    baseAlias: item.alias,
                    variantAlias: null,
                    displayLabel: item.label,
                    svgSrc: item.svgSrc,
                    rotable: item.rotable,
                    symbolLabel: item.type,
                    source: item,
                });
            }
        });
        return entries;
    };

    const renderCategoryItems = (angleOffset, category, onDragStart) => {
        const rawItems = symbolsCatalog[category] || Symbols[category] || [];
        const displayEntries = buildDisplayEntries(rawItems);

        return displayEntries.map((entry) => (
            <CategoryItem
                key={entry.key}
                entry={entry}
                angleOffset={angleOffset}
                onDragStart={onDragStart}
                category={category}
                onSingleClick={handlePaletteSingleClick}
                onDoubleClick={handlePaletteDoubleClick}
            />
        ));
    };    

    const toggleCategory = (category) => (event) => {
        event.preventDefault();
        const newStatus = !categories[category].open;
        const scrollRoot = toolsScrollRef.current;
        const currentScrollTop = scrollRoot ? scrollRoot.scrollTop : 0;
        dispatch(setExclusiveCategoryOpen({ category: category, open: newStatus }));
        if (scrollRoot) {
            requestAnimationFrame(() => {
                scrollRoot.scrollTop = currentScrollTop;
            });
        }
    };

    // TODO: Trim
    if(searchQuery == "" && !forceSearchResults) {
        return (
            <div className='ctn-tools' data-scroll-root ref={toolsScrollRef}>
                {/* <div className="symbols-rotate-toggle">
                    <button type="button" className="symbols-rotate-toggle__btn" onClick={toggleSymbolsRotation}>
                        <span>Rotar símbolos</span>
                    </button>
                </div> */}
                <Dropdown
                    key="EnergySources"
                    title="Fuentes de energía"
                    isOpen={categories.energySources.open}
                    onClick={toggleCategory("energySources")}>
                    {renderCategoryItems(angleOffset, "electricEnergySuppliers", handleDragStart)}
                </Dropdown>

                <Dropdown
                    key="Transformers"
                    title="Transformadores"
                    isOpen={categories.transformers.open}
                    onClick={toggleCategory("transformers")}>
                    {renderCategoryItems(angleOffset, "twoWindingTransformers", handleDragStart)}
                    {renderCategoryItems(angleOffset, "threeWindingTransformers", handleDragStart)}
                </Dropdown>
        
                <Dropdown
                    key="Charges"
                    title="Cargas"
                    isOpen={categories.charges.open}
                    onClick={toggleCategory("charges")}>
                    {renderCategoryItems(angleOffset, "generators", handleDragStart)}
                </Dropdown>
        
                <Dropdown
                    key="ElectronicPotency"
                    title="Electrónica de potencia"
                    isOpen={categories.electronicPotency.open}
                    onClick={toggleCategory("electronicPotency")}>
                    {renderCategoryItems(angleOffset, "electronicRelays", handleDragStart)}
                </Dropdown>
        
                <Dropdown
                    key="ProtectionDevices"
                    title="Protección contra sobrecorrientes"
                    isOpen={categories.protectionDevices.open}
                    onClick={toggleCategory("protectionDevices")}>
                    {renderCategoryItems(angleOffset, "electromagneticSwitches", handleDragStart)}
                    {renderCategoryItems(angleOffset, "fuses", handleDragStart)}
                </Dropdown>
        
                <Dropdown
                    key="Filters"
                    title="Bancos y Dispositivos de Compensación"
                    isOpen={categories.filters.open}
                    onClick={toggleCategory("filters")}>
                    {renderCategoryItems(angleOffset, "overloadRelays", handleDragStart)}
                </Dropdown>
        
                <Dropdown
                    key="Misc"
                    title="Equipos de distribución"
                    isOpen={categories.misc.open}
                    onClick={toggleCategory("misc")}>
                    {renderCategoryItems(angleOffset, "vfds", handleDragStart)}
                    {renderCategoryItems(angleOffset, "cables", handleDragStart)}
                    {renderCategoryItems(angleOffset, "buses", handleDragStart)}
                    {renderCategoryItems(angleOffset, "distributionBoards", handleDragStart)}
                </Dropdown>

                <Dropdown
                    key="Medicion"
                    title="Medicion"
                    isOpen={categories.medicion.open}
                    onClick={toggleCategory("medicion")}>

                    {renderCategoryItems(angleOffset, "lightningContactChargingCenters", handleDragStart)}
                </Dropdown>
            </div>
        );
    } else {
        // If forced results and empty query, show hint message instead of listing all
        if (forceSearchResults && (searchQuery || '').trim() === '') {
          return (
            <div className="search-results-wrapper" style={{ padding: 12 }} data-scroll-root>
              {/* <div className="symbols-rotate-toggle symbols-rotate-toggle__results">
                <button type="button" className="symbols-rotate-toggle__btn" onClick={toggleSymbolsRotation}>
                  <img
                    src="/assets/ico-rotar.png"
                    alt=""
                    className="symbols-rotate-toggle__icon"
                    style={{ transform: angleOffset === VERTICAL_ORIENTATION ? 'rotate(0deg)' : 'rotate(90deg)' }}
                  />
                  <span>Rotar símbolos</span>
                </button>
              </div> */}
              <div style={{
                padding: '12px 14px',
                borderRadius: 8,
                background: '#f5f7fa',
                color: '#444',
                fontSize: 14
              }}>
                Busca tu símbolo y presiona enter para agregar en el editor
              </div>
            </div>
          );
        }
        const linear = Array.isArray(searchResults) ? searchResults : [];
        return (
          <div className="search-results-wrapper" data-scroll-root>
            {/* <div className="symbols-rotate-toggle symbols-rotate-toggle__results">
              <button type="button" className="symbols-rotate-toggle__btn" onClick={toggleSymbolsRotation}>
                <img
                  src="/assets/ico-rotar.png"
                  alt=""
                  className="symbols-rotate-toggle__icon"
                  style={{ transform: angleOffset === VERTICAL_ORIENTATION ? 'rotate(0deg)' : 'rotate(90deg)' }}
                />
                <span>Rotar símbolos</span>
              </button>
            </div> */}
            <div className="search-results-grid">
              {linear.map((item, index) => {
                const isSelected = index === selectedIndex;
                return (
                  <div key={`sr-${index}`}>
                    <CategoryItem
                      entry={item}
                      angleOffset={angleOffset}
                      onDragStart={handleDragStart}
                      category={item.category}
                      displayIndex={index + 1}
                      onSingleClick={handlePaletteSingleClick}
                      onDoubleClick={handlePaletteDoubleClick}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );        
    }
    
};

export default EditorTabPanelElements;
